import {
  Children,
  isValidElement,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
  type PropsWithChildren,
} from "react";
import { ActionIcon, DetailCard, ServerPagination, StatusPill } from "./primitives";
import { RECORDS_PER_PAGE } from "@taunggyi-plus/domain";

export function RecordFormDialog({
  title,
  children,
  triggerLabel,
  action,
  open,
  onClose,
  hideTrigger = false,
  className = "",
}: PropsWithChildren<{
  title: ReactNode;
  triggerLabel?: string;
  action?: "add" | "filter" | "edit";
  open?: boolean;
  onClose?: () => void;
  hideTrigger?: boolean;
  className?: string;
}>) {
  const dialog = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  useEffect(() => {
    if (open === true) dialog.current?.showModal();
    else if (open === false) dialog.current?.close();
  }, [open]);
  const label = triggerLabel ?? (typeof title === "string" ? title : "Add record");
  return (
    <div className="record-form-action">
      {!hideTrigger && (
        <button
          type="button"
          className="toolbar-icon-button"
          aria-label={label}
          title={label}
          aria-haspopup="dialog"
          onClick={() => dialog.current?.showModal()}
        >
          <ActionIcon
            action={action ?? (/^(edit|change|update)/i.test(String(title)) ? "edit" : "add")}
          />
        </button>
      )}
      <dialog
        ref={dialog}
        onClose={onClose}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            event.preventDefault();
            dialog.current?.close();
          }
        }}
        className={`record-modal customer-order-filter-dialog subscription-popup ${className}`}
        aria-labelledby={titleId}
      >
        <header>
          <h2 id={titleId}>{title}</h2>
          <button
            type="button"
            className="toolbar-icon-button"
            aria-label="Close dialog"
            onClick={() => dialog.current?.close()}
          >
            ×
          </button>
        </header>
        <div className="record-modal__body">{children}</div>
      </dialog>
    </div>
  );
}

export interface RecordColumn<T> {
  key: string;
  label: string;
  render: (record: T) => ReactNode;
}
export function RecordTable<T>({
  items,
  columns,
  rowKey,
  label = "Records",
  resetKey = "",
  empty = "No records found.",
}: {
  items: readonly T[];
  columns: readonly RecordColumn<T>[];
  rowKey: (record: T) => string;
  label?: string;
  resetKey?: string;
  empty?: string;
}) {
  return (
    <RecordTablePage
      key={resetKey}
      items={items}
      columns={columns}
      rowKey={rowKey}
      label={label}
      empty={empty}
    />
  );
}
function RecordTablePage<T>({
  items,
  columns,
  rowKey,
  label,
  empty,
}: Omit<Parameters<typeof RecordTable<T>>[0], "resetKey">) {
  const [page, setPage] = useState(0);
  const current = Math.min(page, Math.max(0, Math.ceil(items.length / RECORDS_PER_PAGE) - 1));
  return (
    <div className="record-table">
      <div className="record-table__scroll" role="region" aria-label={label} tabIndex={0}>
        <table>
          <caption className="visually-hidden">{label}</caption>
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column.key} scope="col">
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.length ? (
              items.slice(0, (current + 1) * RECORDS_PER_PAGE).map((item) => (
                <tr key={rowKey(item)}>
                  {columns.map((column) => (
                    <td key={column.key} data-label={column.label}>
                      {column.render(item)}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={columns.length} className="record-table__empty">
                  {empty}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {items.length > RECORDS_PER_PAGE && (
        <ServerPagination page={current} total={items.length} onPage={setPage} />
      )}
    </div>
  );
}

export function formatRecordDate(
  value: string | null | undefined,
  locale = "en",
  withTime = false,
) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  const language = locale === "my" ? "my-MM" : locale === "zh" ? "zh-CN" : "en-GB";
  return new Intl.DateTimeFormat(language, {
    day: "numeric",
    month: "short",
    year: "numeric",
    ...(withTime ? ({ hour: "2-digit", minute: "2-digit" } as const) : { timeZone: "UTC" }),
  }).format(date);
}

/** Present compact summaries with refreshable detail URLs. */
export function RecordCollection({ records, label }: { records: ReactNode[]; label: string }) {
  const parameters = new URLSearchParams(window.location.search);
  const selected = parameters.get("record");
  const recordId = (record: ReactNode, index: number) =>
    isValidElement(record) ? String(record.key ?? index) : String(index);
  const selectedRecord =
    parameters.get("list") === label ? records.find((r, i) => recordId(r, i) === selected) : null;
  const back = new URL(window.location.href);
  back.searchParams.delete("record");
  back.searchParams.delete("list");
  if (selectedRecord)
    return (
      <article className="record-detail-page">
        <a className="more-back-link" href={back.pathname + back.search}>
          ← Back to records
        </a>
        {isValidElement<{ children?: ReactNode }>(selectedRecord) &&
        selectedRecord.type === DetailCard
          ? selectedRecord.props.children
          : selectedRecord}
      </article>
    );
  return (
    <div className="record-table record-table--collection">
      <div className="record-table__scroll" role="region" aria-label={label} tabIndex={0}>
        <table>
          <caption className="visually-hidden">{label}</caption>
          <thead>
            <tr>
              <th scope="col">Record</th>
              <th scope="col">Status</th>
              <th scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {records.length ? (
              records.map((record, index) => {
                const url = new URL(window.location.href);
                url.searchParams.set("list", label);
                url.searchParams.set("record", recordId(record, index));
                return (
                  <RecordCollectionRow
                    key={recordId(record, index)}
                    record={record}
                    href={url.pathname + url.search}
                  />
                );
              })
            ) : (
              <tr>
                <td colSpan={3}>No records found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function findRecordNode(node: ReactNode, predicate: (type: unknown) => boolean): ReactNode {
  for (const child of Children.toArray(node)) {
    if (!isValidElement<{ children?: ReactNode }>(child)) continue;
    if (predicate(child.type)) return child.props.children;
    const nested = findRecordNode(child.props.children, predicate);
    if (nested) return nested;
  }
  return null;
}
function RecordCollectionRow({ record, href }: { record: ReactNode; href: string }) {
  const detail =
    isValidElement<{ title?: string; summary?: ReactNode; children?: ReactNode }>(record) &&
    record.type === DetailCard
      ? record.props
      : null;
  const heading =
    detail?.title ??
    findRecordNode(record, (type) => type === "h3" || type === "h2" || type === "strong");
  const summary =
    findRecordNode(record, (type) => type === "p") ??
    findRecordNode(record, (type) => type === "small");
  const status = findRecordNode(detail?.summary ?? record, (type) => type === StatusPill);
  return (
    <tr>
      <td>
        <div className="record-table__name">{heading ?? summary ?? "Record"}</div>
        {heading && summary && <div className="record-table__secondary">{summary}</div>}
      </td>
      <td data-label="Status">{status ? <StatusPill>{status}</StatusPill> : "—"}</td>
      <td>
        <a className="button button--secondary" href={href} aria-label="View details">
          View
        </a>
      </td>
    </tr>
  );
}
